'use client';

import * as z from 'zod';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useBoolean } from 'minimal-shared/hooks';
import { zodResolver } from '@hookform/resolvers/zod';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { parseValidationErrors } from 'src/utils/parse-validation-errors';

import { useTranslate } from 'src/locales';

import { Iconify } from 'src/components/iconify';
import { Form, Field, schemaUtils } from 'src/components/hook-form';

import { signUp } from '../../context/jwt';
import { getErrorMessage } from '../../utils';
import { FormHead } from '../../components/form-head';
import { SignUpTerms } from '../../components/sign-up-terms';

// ----------------------------------------------------------------------

export type SignUpSchemaType = {
  name: string;
  email: string;
  password: string;
  company_name: string;
  account_type: 'company';
};

// ----------------------------------------------------------------------

export function JwtSignUpView() {
  const { t } = useTranslate();

  const showPassword = useBoolean();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const SignUpSchema = useMemo(
    () =>
      z.object({
        name: z.string().min(2, { error: t('auth.validation.nameMin') }).max(100),
        email: schemaUtils.email(),
        password: z.string().min(8, { error: t('auth.validation.passwordMin8') }).max(72),
        company_name: z
          .string()
          .min(2, { error: t('auth.validation.companyMin') })
          .max(100),
        account_type: z.enum(['company']),
      }),
    [t]
  );

  const defaultValues: SignUpSchemaType = {
    name: '',
    email: '',
    password: '',
    company_name: '',
    account_type: 'company',
  };

  const methods = useForm<SignUpSchemaType>({
    resolver: zodResolver(SignUpSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      await signUp({
        email: data.email,
        password: data.password,
        company_name: data.company_name,
        name: data.name,
        account_type: data.account_type,
      });
      setSuccessMessage(t('auth.signUpSuccess'));
      reset(defaultValues);
    } catch (error: any) {
      // How to use parseValidationErrors with React Hook Form:
      if (error.code === 'validation_error' && error.details) {
        const fieldErrors = parseValidationErrors(error.details);
        Object.entries(fieldErrors).forEach(([field, message]) => {
          methods.setError(field as any, { message });
        });
      } else {
        setErrorMessage(getErrorMessage(error));
      }
    }
  });

  const renderForm = () => (
    <Box sx={{ gap: 3, display: 'flex', flexDirection: 'column' }}>
      <Field.Text
        name="name"
        label={t('auth.fullName')}
        slotProps={{ inputLabel: { shrink: true } }}
      />

      <Field.Text
        name="email"
        label={t('auth.email')}
        slotProps={{ inputLabel: { shrink: true } }}
      />

      <Field.Text
        name="company_name"
        label={t('auth.companyName')}
        slotProps={{ inputLabel: { shrink: true } }}
      />

      <Field.Text
        name="password"
        label={t('auth.password')}
        placeholder={t('auth.passwordHint8')}
        type={showPassword.value ? 'text' : 'password'}
        slotProps={{
          inputLabel: { shrink: true },
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={showPassword.onToggle} edge="end">
                  <Iconify icon={showPassword.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'} />
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
      />

      <Button
        fullWidth
        color="inherit"
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitting}
        loadingIndicator={t('auth.creatingAccount')}
      >
        {t('auth.createAccountButton')}
      </Button>
    </Box>
  );

  return (
    <>
      <FormHead
        title={t('auth.createAccountTitle')}
        description={
          <>
            {`${t('auth.haveAccount')} `}
            <Link component={RouterLink} href={paths.auth.jwt.signIn} variant="subtitle2">
              {t('auth.signIn')}
            </Link>
          </>
        }
        sx={{ textAlign: { xs: 'center', md: 'left' } }}
      />

      {!!errorMessage && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {errorMessage}
        </Alert>
      )}

      {!!successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      <Form methods={methods} onSubmit={onSubmit}>
        {renderForm()}
      </Form>

      <SignUpTerms />
    </>
  );
}
